/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { memo, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { dateFormate } from "util/Util";
import Modal from "components/Modal";
import apiService from "services/apiService";
import { skillService } from "./skillService";
import { useToaster } from "contexts/toasterContext";
import { OptionType } from "./Skills";
import SelectSkillForm from "./SelectSkillForm";
import GenericModal from "components/Modal";
import DeletePopup from "components/DeletePopup/deletePopup";
import {
  DENSE_FIELD_GRID_CLASS,
  EditButton,
  EmptyState,
  InfoCard,
  InfoField,
  InfoFieldGrid,
} from "./components/ProfileInfo";
import Button from "components/Button";
import BankDetails from "./BankDetails";
import FamilyDetails from "./FamilyDetails";
import { X } from "lucide-react";
import { TruncatedText } from "pages/AdminConfig/components/TruncatedText";

// ── Interface ─────────────────────────────────────────────────────────────────

const GRID_CELL = "grid min-h-0 min-w-0 grid-rows-[minmax(0,1fr)]";

interface ProfileCardProps {
  user: any;
  onEditPersonal?: () => void;
  onUserUpdated?: (user: any) => void;
}

// ── Component ─────────────────────────────────────────────────────────────────

/**
 * "My Profile" tab content — personal, bank and family details and skills.
 * The profile photo / name / designation header now lives in ProfileHeader,
 * rendered above the tabs.
 */
function ProfileCard({ user, onEditPersonal, onUserUpdated }: Readonly<ProfileCardProps>) {
  const { t } = useTranslation();
  const [userSkills, setUserSkills] = useState<OptionType[]>([]);
  const [isAddingSkill, setIsAddingSkill] = useState(false);
  const [, setSelectedSkills] = useState<OptionType[]>([]);
  const [openDeleteModal, setDeleteModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState<OptionType | null>(null);
  const { addToast } = useToaster();
  const hasSkills = Array.isArray(userSkills) && userSkills.length > 0;
  const openSkillModal = () => setIsAddingSkill(true);

  const fetchUserSkill = () => {
    if (!user?.id) return;
    apiService
      .get(`api/user/skill/${user.id}`)
      .then((res) => {
        const userSkillsData = res.data.map((skill: any) => ({
          label: skill.skill,
          value: skill.skill,
          id: String(skill.id),
        }));
        setUserSkills(userSkillsData);
      })
      .catch((err) => {
        console.log("Error fetching user skills", err);
      });
  };

  useEffect(() => {
    fetchUserSkill();
  }, [user?.id]);

  const deleteSkill = (skillToRemove: OptionType | null) => {
    console.log("skillToRemove", skillToRemove);
    if (!skillToRemove?.id) {
      addToast("Unable to delete skill: missing skill id", "error");
      return;
    }
    const skillId = String(skillToRemove.id);
    const urlencoded = new URLSearchParams();
    urlencoded.append("userId", String(user.id));
    urlencoded.append("skillId", skillId);
    skillService
      .deleteWithBody(`/api/user/skill?userId=${user.id}&skillId=${skillId}`, urlencoded)
      .then((res) => {
        console.log("res", res);
        setDeleteModal(false);
        addToast("Skill deleted successfully", "success");
        setUserSkills((prev) => prev.filter((skill) => skill.id !== skillId));
        fetchUserSkill();
      })
      .catch((err) => {
        console.error("err", err);
        addToast(`${err.response.data}`, "error");
      });
  };

  return (
    <div className="w-full py-1 pr-4 flex flex-col md:h-full">
      {/* ── Data cards — aligned to the Employee Details layout ── */}
      <div className="grid grid-cols-1 gap-4 md:flex-1 md:min-h-0 md:grid-cols-2 md:grid-rows-[auto_minmax(0,1fr)]">
        <div className={`${GRID_CELL} order-1`}>
          <InfoCard
            title="Personal Details"
            action={onEditPersonal && <EditButton onClick={onEditPersonal} />}
          >
            <InfoFieldGrid className={DENSE_FIELD_GRID_CLASS}>
              <InfoField label={t("profile.emailText")} value={user.email} />
              <InfoField label={t("profile.mobileText")} value={user.mobileNumber} />
              <InfoField label={t("profile.dobText")} value={dateFormate(user.DOB)} />
              <InfoField label={t("profile.doj")} value={dateFormate(user.DOJ)} />
              <InfoField
                label={t("profile.addressText")}
                value={
                  <TruncatedText
                    text={user.permanentAddress || "-"}
                    className="w-[200px] sm:w-[250px] lg:w-[300px] cursor-pointer"
                  />
                }
              />
              <InfoField label={t("profile.cityText")} value={user.city} />
              <InfoField label={t("profile.stateText")} value={user.state} />
              <InfoField label={t("profile.pincodeText")} value={user.pinCode} />
              <InfoField label="PAN Number" value={user.PAN} />
              <InfoField label="Aadhaar Number" value={user.profileDetails?.aadhaar} />
              <InfoField label="UAN Number" value={user.UAN} />
              <InfoField label="Emp ID" value={user.employeeId ?? user.empId} />
            </InfoFieldGrid>
          </InfoCard>
        </div>

        <div className={`${GRID_CELL} order-3 md:order-2 md:[contain:size]`}>
          <FamilyDetails user={user} />
        </div>

        <div className={`${GRID_CELL} order-2 md:order-3`}>
          <BankDetails user={user} onUserUpdated={onUserUpdated} />
        </div>

        <div className={`${GRID_CELL} order-4`}>
          <InfoCard
            title="Skills"
            action={hasSkills && <EditButton onClick={openSkillModal} />}
          >
            {hasSkills ? (
              <div className="p-4 flex flex-wrap gap-2">
                {userSkills.map((skill: any, index: number) => (
                  <span
                    key={skill?.id || index}
                    className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-700"
                  >
                    {skill?.label || skill?.name || "Unknown Skill"}
                    <button
                      onClick={() => {
                        setSelectedRow(skill);
                        setDeleteModal(true);
                      }}
                      className="text-blue-400 hover:text-red-500 dark:text-blue-400 dark:hover:text-red-400 transition-colors"
                      title="Remove skill"
                      aria-label={`Remove ${skill?.label || skill?.name}`}
                    >
                      <X size={11} />
                    </button>
                  </span>
                ))}
              </div>
            ) : (
              <EmptyState message="No skills added yet.">
                <Button
                  onClick={openSkillModal}
                  type="button"
                  className="!bg-blue-600 hover:!bg-blue-700 !text-white shadow-md"
                >
                  Add Skills
                </Button>
              </EmptyState>
            )}
          </InfoCard>
        </div>
      </div>

      {isAddingSkill && (
        <Modal
          isOpen={isAddingSkill}
          onClose={() => { setIsAddingSkill(false); setSelectedSkills([]); }}
          header="Add Skills"
          custemHeight="auto"
        >
          <SelectSkillForm
            onSuccess={() => { setIsAddingSkill(false); setSelectedSkills(userSkills); fetchUserSkill(); }}
            existingSkills={userSkills.map((skill) => ({
              id: Number(skill.id),
              skill: skill.label,
            }))}
            onError={() => setIsAddingSkill(false)}
          />
        </Modal>
      )}

      {openDeleteModal && (
        <GenericModal
          isOpen={openDeleteModal}
          onClose={() => setDeleteModal(false)}
          header="Delete Skill"
        >
          <DeletePopup
            handleDelete={() => deleteSkill(selectedRow)}
            onClose={() => setDeleteModal(false)}
            selectedRow={selectedRow}
          />
        </GenericModal>
      )}
    </div>
  );
}

export default memo(ProfileCard);
